import DraggableWidget from "./DraggableWidget";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface ScoreWidgetProps {
  id: string;
  matchup?: {
    home_team: string;
    away_team: string;
    home_score: number;
    away_score: number;
    status: string;
    win_probability?: number;
  };
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
}

const ScoreWidget = ({ id, matchup, onDragStart, onDragEnd, isDragging }: ScoreWidgetProps) => {
  if (!matchup) {
    return (
      <DraggableWidget
        id={id}
        title="Score & Win Probability"
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        isDragging={isDragging}
      >
        <div className="text-center text-muted-foreground">
          No match data available
        </div>
      </DraggableWidget>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return <Badge className="bg-red-500 animate-pulse">Live</Badge>;
      case 'final':
        return <Badge variant="secondary">Final</Badge>;
      default:
        return <Badge variant="outline">Upcoming</Badge>;
    }
  };

  return (
    <DraggableWidget
      id={id}
      title="Score & Win Probability"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      isDragging={isDragging}
    >
      <div className="space-y-4">
        {/* Match Status */}
        <div className="text-center">
          {getStatusBadge(matchup.status)}
        </div>

        {/* Score */}
        <div className="text-center">
          <div className="grid grid-cols-3 items-center gap-4">
            <div className="text-right">
              <div className="font-semibold">{matchup.home_team}</div>
            </div>
            <div className="text-3xl font-mono font-bold">
              <span>{matchup.home_score}</span>
              <span className="text-muted-foreground mx-2">-</span>
              <span>{matchup.away_score}</span>
            </div>
            <div className="text-left">
              <div className="font-semibold">{matchup.away_team}</div>
            </div>
          </div>
        </div>

        {/* Win Probability */}
        {matchup.win_probability && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-center">Win Probability</div>
            <Progress value={matchup.win_probability} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{matchup.home_team}: {matchup.win_probability.toFixed(1)}%</span>
              <span>{matchup.away_team}: {(100 - matchup.win_probability).toFixed(1)}%</span>
            </div>
          </div>
        )}
      </div>
    </DraggableWidget>
  );
};

export default ScoreWidget;